(ns minorodata.transform-environments.api
  (:require [clojure.set :refer [rename-keys]]
            [clojure.string]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.posthog-events :as posthog-events]
            [minorodata.app.utils :as utils]
            [minorodata.group.model :refer [->group ->group-transforms
                                            ->groups-panel]]
            [minorodata.transform-environments.events :refer [valid-env-id]]
            [minorodata.transform-environments.model :refer [->environment
                                                             ->environments-panel
                                                             ->transform-diff]]
            [minorodata.transform.api :refer [schedule-change-capture select-tables]]
            [minorodata.transform.model :refer [->transform ->transforms-panel]]
            [mold-http.core :refer [reg-event-http]]
            [re-frame.core :refer [reg-event-fx]]
            [re-posh.core :as re-posh]))


;; =============================================================================
;; Fetch all environments
;; =============================================================================

(defn fetch-environments-request []
  {:http-xhrio
   {:id     :fetch-environments-request
    :url    (str "/api/transformations/environments")
    :method :get}})

(defn fetch-environments-callback [_ [error resp]]
  (if (some? error)
    (let [message "Failed to fetch environments"]
      {:dispatch [:app.events/add-snack :error message]})

    (let [envs (mapv ->environment (:results resp))]
      {:dispatch-n [[:environments-panel/update (->environments-panel {:environments envs}) {:replace true}]
                    [:environment/add-many envs]

                    ;; Make sure active environment exists
                    [:environments.events/validate-environment envs]]})))

(reg-event-http
 :environments.api/fetch-environments
 fetch-environments-request
 fetch-environments-callback)


;; =============================================================================
;; Fetch single environment
;; =============================================================================

(defn fetch-query-params [params panel]
  (let [{:groups-panel/keys [page order-by order-dir]} panel
        sort (cond-> nil
               (some? order-by) (assoc :order-by order-by)
               (some? order-dir) (assoc :order-dir order-dir)
               (some? (:order-by params)) (assoc :order-by (:order-by params))
               (some? (:order-dir params)) (assoc :order-dir (:order-dir params)))]
    (utils/assoc-some {:pagination {:page           (or (:page params) page 1)
                                    :items-per-page (or (:items-per-page params) 50)}}
                      :sort sort)))

(defn fetch-environment-request [_ [{:keys [page]
                                     :as   params}]]
  (let [panel @(re-posh/subscribe [:groups-panel/pull])
        environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    (when (or (nil? page)
              (= 1 page)
              (<= page (:groups-panel/total-pages panel)))
      {:http-xhrio
       {:id     :fetch-environment-request
        :url    (str "/api/transformations/environments/" environment-id)
        :params (fetch-query-params params panel)
        :method :get
        :cb-params params}})))

(defn fetch-environment-callback [_ [error resp {:keys [replace?]}]]
  (if (some? error)
    (let [message "Failed to fetch environment"]
      {:dispatch [:app.events/add-snack :error message]})

    (let [{:groups-panel/keys [latest-order-index]} @(re-posh/subscribe [:groups-panel/pull])

          {:keys [groups modification-states pagination sort]} (:result resp)
          groups              (mapv #(rename-keys % {:transform-group-id :id}) groups)
          results-count       (count groups)
          [start-idx end-idx] (if replace?
                                [0 results-count]
                                [latest-order-index (+ latest-order-index results-count)])
          groups              (mapv (fn [idx group] (assoc group :order idx))
                                    (range start-idx end-idx)
                                    groups)
          panel               (-> {:groups             groups
                                   :latest-order-index end-idx}
                                  (merge pagination sort)
                                  ->groups-panel)]
      {:dispatch-n [[:environments-panel/update (->environments-panel {:modification-states modification-states})]
                    [:groups-panel/update panel {:replace replace?}]]})))

(reg-event-http
 :environments.api/fetch-environment
 fetch-environment-request
 fetch-environment-callback)


(reg-event-fx
 :environments.api/fetch-groups
 (fn [_ [_ params]]
   (let [prod-env?      @(re-posh/subscribe [:environments.subs/production-active?])]
     (if prod-env?
       {:dispatch [:group.api/fetch-groups params]}
       {:dispatch [:environments.api/fetch-environment params]}))))


;; =============================================================================
;; Create environment
;; =============================================================================

(defn create-environment-request [_ [{:keys [data with-data? on-success]}]]
  {:http-xhrio
   {:id     :create-environment-request
    :url    "/api/transformations/environments"
    :method :post
    :params data
    :cb-params {:on-success on-success
                :with-data? with-data?}}})

(defn create-environment-callback [_ [error resp {:keys [on-success with-data?]}]]
  (if (some? error)
    (let [message (get-in error [:response :error])]
      {:dispatch [:app.events/add-snack :error (cond-> "Failed to execute query"
                                                 message (str ": " message))]})

    (let [environment (->environment (:result resp))]
      (when on-success (on-success environment))
      {:dispatch-n [[:app.events/add-snack :success "Environment created successfully"]
                    [:environments.api/fetch-environments]]
       :posthog/capture {:event (:transform-environment/created posthog-events/events)
                         :props {:environment-id (:environment/environment-id environment)
                                 :with-data      (boolean with-data?)}}})))

(reg-event-http
 :environments.api/create-environment
 create-environment-request
 create-environment-callback)


;; =============================================================================
;; Delete environment
;; =============================================================================

(defn delete-environment-request [_ [{:keys [environment-id on-success]}]]
  {:http-xhrio
   {:id     :delete-environment-request
    :url    (str "/api/transformations/environments/" environment-id)
    :method :delete
    :cb-params {:environment-id environment-id
                :on-success     on-success}}})

(defn delete-environment-callback [_ [error _ {:keys [environment-id on-success]}]]
  (if (some? error)
    (let [message "Failed to delete environment"]
      {:dispatch [:app.events/add-snack :error message]})

    (do
      (when on-success (on-success environment-id))
      {:dispatch-n [[:app.events/add-snack :success "Environment deleted successfully"]
                    [:environment/delete [:environment/environment-id environment-id]]]
       :posthog/capture {:event (:transform-environment/deleted posthog-events/events)
                         :props {:environment-id environment-id}}})))

(reg-event-http
 :environments.api/delete-environment
 delete-environment-request
 delete-environment-callback)


;; =============================================================================
;; Fetch single environment transforms
;; =============================================================================

(defn fetch-transforms-request [_ [{:keys [group-id page diff?]
                                    :as   params}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])
        panel @(re-posh/subscribe [:transforms-panel/pull])]
    (when (or (nil? page)
              (<= page (:transforms-panel/total-pages panel))
              diff?)
      {:http-xhrio
       {:id        :fetch-transform-group-request
        :url       (str "/api/transformations/environments/" environment-id "/" group-id "/transforms")
        :method    :get
        :params    (fetch-query-params params panel)
        :cb-params params}})))

(defn fetch-transforms-callback [_ [error resp {:keys [group-id replace? diff?]}]]
  (if (some? error)
    (let [message "Failed to fetch environment transform group"]
      {:dispatch [:app.events/add-snack :error message]})

    (let [{:transforms-panel/keys [latest-order-index]} @(re-posh/subscribe [:transforms-panel/pull])
          {:keys [results pagination sort]} resp

          results-count   (count results)
          [start-idx end-idx] (if replace?
                                [0 results-count]
                                [latest-order-index (+ latest-order-index results-count)])
          transforms      (mapv (fn [idx transform] (assoc transform :order idx :id (:transform-id transform)))
                                (range start-idx end-idx)
                                results)
          panel      (-> {:transforms         transforms
                          :latest-order-index end-idx}
                         (merge pagination sort)
                         ->transforms-panel)
          update-previous (mapv (fn [{:keys [id] :as transform}]
                                  [:transform/update [:transform/id id] (->transform transform) {:replace true}])
                                transforms)]
      {:dispatch-n (cond-> update-previous
                     (not diff?) (conj [:transforms-panel/update panel {:replace replace?}])
                     true        (conj [:transform/add-many transforms]
                                       [:group-transforms/delete [:group-transforms/id group-id]]
                                       [:group-transforms/add (->group-transforms {:id         group-id
                                                                                   :transforms transforms})]))})))

(reg-event-http
 :environments.api/fetch-environment-transforms
 fetch-transforms-request
 fetch-transforms-callback)

(reg-event-fx
 :environments.api/fetch-transforms
 (fn [_ [_ params]]
   (let [prod-env? @(re-posh/subscribe [:environments.subs/production-active?])]
     (if prod-env?
       {:dispatch [:transform.api/fetch-transforms params]}
       {:dispatch [:environments.api/fetch-environment-transforms params]}))))

(defn fetch-diff-transforms-request [db [params]]
  (fetch-transforms-request db [(assoc params :diff? true)]))

(reg-event-http
 :environments.api/fetch-diff-transforms
 fetch-diff-transforms-request
 fetch-transforms-callback)

;; =============================================================================
;; Create environment transform group
;; =============================================================================

(defn create-transform-group-request [_ [{:keys [data]}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    {:http-xhrio
     {:id     :create-transform-group-request
      :url    (str "/api/transformations/environments/" environment-id)
      :method :post
      :params data
      :cb-params {:environment-id environment-id}}}))

(defn create-transform-group-callback [_ [error resp {:keys [environment-id]}]]
  (if (some? error)
    (let [message "Failed to create environment transform group"]
      {:dispatch [:app.events/add-snack :error message]})

    (let [{:groups-panel/keys [latest-order-index]} @(re-posh/subscribe [:groups-panel/pull])
          {:keys [id] :as group} (:result resp)
          group-idx  (or latest-order-index 0)
          full-group (-> group
                         (assoc :order group-idx)
                         ->group)]

      {:dispatch-n [[:group/add full-group]
                    [:groups-panel/update {:groups-panel/groups             {:group/id id}
                                           :groups-panel/latest-order-index (+ group-idx 1)}]
                    [:app.events/add-snack :success "Group created"]]
       :posthog/capture {:event (:transform/group-created posthog-events/events)
                         :props {:environment-id environment-id}}})))

(reg-event-http
 :environments.api/create-environment-group
 create-transform-group-request
 create-transform-group-callback)

(reg-event-fx
 :environments.api/create-group
 (fn [_ [_ params]]
   (let [prod-env? @(re-posh/subscribe [:environments.subs/production-active?])]
     (if prod-env?
       {:dispatch [:group.api/create-group (:data params) (dissoc params :data)]}
       {:dispatch [:environments.api/create-environment-group params]}))))


;; =============================================================================
;; Update environment transform group
;; =============================================================================

(defn update-group-request [_ [{:keys [data on-success]}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    {:http-xhrio
     {:id     :update-group-request
      :url    (str "/api/transformations/environments/" environment-id "/" (:id data))
      :method :put
      :params data
      :cb-params {:on-success on-success}}}))

(defn update-group-callback [_ [error resp {:keys [on-success]}]]
  (if (some? error)
    (let [message "Failed to update group"]
      {:dispatch [:app.events/add-snack :error message]})

    (let [group (-> (:result resp) (rename-keys {:transform-group-id :id}) ->group)]
      (when on-success (on-success group))
      {:dispatch-n [[:app.events/add-snack :success "Transform group updated successfully"]
                    [:group/add group {:replace true}]]})))

(reg-event-http
 :environments.api/update-environment-group
 update-group-request
 update-group-callback)

(reg-event-fx
 :environments.api/update-group
 (fn [_ [_ params]]
   (let [prod-env? @(re-posh/subscribe [:environments.subs/production-active?])]
     (if prod-env?
       {:dispatch [:group.events/save-group-updates (:data params) (dissoc params :data)]}
       {:dispatch [:environments.api/update-environment-group params]}))))


;; =============================================================================
;; Drop environment transform group
;; =============================================================================

(defn delete-transform-group-request [_ [{:keys [group-id on-success]}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    {:http-xhrio
     {:id     :delete-transform-group-request
      :url    (str "/api/transformations/environments/" environment-id "/" group-id)
      :method :delete
      :cb-params {:group-id   group-id
                  :on-success on-success}}}))

(defn delete-transform-group-callback [_ [error _ {:keys [group-id on-success]}]]
  (if (some? error)
    (let [message "Failed to delete environment transform group"]
      {:dispatch [:app.events/add-snack :error message]})

    (do
      (when on-success (on-success group-id))
      {:dispatch-n [[:app.events/add-snack :success "Environment transform group deleted successfully"]
                    [:app.events/navigate :transformations]
                    [:group/delete [:group/id group-id]]]})))

(reg-event-http
 :environments.api/delete-environment-group
 delete-transform-group-request
 delete-transform-group-callback)

(reg-event-fx
 :environments.api/delete-group
 (fn [_ [_ params]]
   (let [prod-env? @(re-posh/subscribe [:environments.subs/production-active?])]
     (if prod-env?
       {:dispatch [:group.api/delete-group (assoc params :new-page? true)]}
       {:dispatch [:environments.api/delete-environment-group params]}))))


;; =============================================================================
;; Create environment transform
;; =============================================================================

(defn create-transform-request [_ [{:keys [group-id transform] :as params}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])

        {:keys [id days hours minute]} transform
        unsaved-transform @(re-posh/subscribe [:unsaved-transform/pull [:unsaved-transform/id id]])
        {:unsaved-transform/keys [input-tables output-tables]} unsaved-transform]

    {:http-xhrio
     {:id     :create-transform-request
      :url    (str "/api/transformations/environments/" environment-id "/" group-id "/transforms")
      :method :post
      :params (-> transform
                  (assoc :schedule {:days days :hours hours :minute minute})
                  (dissoc :id :days :hours :minute)
                  utils/clean-up-query-recursive
                  (update :command #(or % ""))
                  (update :ignore-parser #(or % false))
                  (update :is-full #(or % false))
                  (update :is-temp #(or % false))
                  (update :status #(or % "active"))
                  (update :input-tables select-tables input-tables)
                  (update :output-tables select-tables output-tables))
      :cb-params (assoc params :environment-id environment-id)}}))

(defn create-transform-callback [_ [error resp {:keys [environment-id on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to create transform"]
                  [:app.api/log :error "Failed to create transform" error]]}

    (let [{:transforms-panel/keys [latest-order-index]} @(re-posh/subscribe [:transforms-panel/pull])
          {:keys [transform-id] :as transform} (:result resp)
          transform-idx  (or latest-order-index 0)
          full-transform (-> transform
                             (rename-keys {:transform-id :id})
                             (assoc :order transform-idx)
                             ->transform)]
      (when on-success (on-success full-transform))
      {:dispatch-n [[:transform/add full-transform]
                    [:unsaved-transform/delete [:unsaved-transform/id "new-transform"]]
                    [:transforms-panel/update {:transforms-panel/transforms         {:transform/id transform-id}
                                               :transforms-panel/latest-order-index (+ transform-idx 1)}]
                    [:app.events/add-snack :success "Transform created"]
                    [:transformations.api/cyclic-deps-check {:show-warnings true}]]
       :posthog/capture {:event (:transform/created posthog-events/events)
                         :props {:environment-id environment-id}}})))

(reg-event-http
 :environments.api/create-environment-transform
 create-transform-request
 create-transform-callback)

(reg-event-fx
 :environments.api/create-transform
 (fn [_ [_ params]]
   (let [prod-env? @(re-posh/subscribe [:environments.subs/production-active?])]
     (if prod-env?
       {:dispatch [:transform.api/create-transform (assoc params :new-page? true)]}
       {:dispatch [:environments.api/create-environment-transform params]}))))


;; =============================================================================
;; Delete environment transform
;; =============================================================================

(defn delete-transform-request [_ [{:keys [group-id transform-id on-success]}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    {:http-xhrio
     {:id     :delete-transform-request
      :url    (str "/api/transformations/environments/" environment-id "/" group-id "/transforms/" transform-id)
      :method :delete
      :cb-params {:on-success   on-success
                  :group-id     group-id
                  :transform-id transform-id}}}))

(defn delete-transform-callback [_ [error _ {:keys [transform-id group-id on-success]}]]
  (cond
    (= 409 (:status error))
    {:dispatch-n [[:app.events/add-snack
                   :warning
                   "Someone has already updated this transform while you were editing it.
                      Please save your changes, and reload the page to get the latest version."]
                  [:transform/update [:transform/id transform-id] {:transform/stale true}]]}

    (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to delete the transform"]
                  [:app.api/log :error "Failed to delete the transform" error]]}

    :otherwise
    (do
      (when on-success (on-success))
      {:dispatch-n [[:environments.api/fetch-transforms {:group-id group-id}]
                    [:app.events/add-snack :success "Transform deleted"]]})))

(reg-event-http
 :environments.api/delete-environment-transform
 delete-transform-request
 delete-transform-callback)

(reg-event-fx
 :environments.api/delete-transform
 (fn [_ [_ params]]
   (let [prod-env? @(re-posh/subscribe [:environments.subs/production-active?])]
     (if prod-env?
       {:dispatch [:transform.api/delete-transform params]}
       {:dispatch [:environments.api/delete-environment-transform params]}))))


;; =============================================================================
;; Update environment transform
;; =============================================================================

(defn update-transform-request [_ [{:keys [group-id environment-id transform] :as params}]]
  (let [environment-id (or environment-id
                           @(re-posh/subscribe [:environments.subs/active-environment-id]))

        {:keys [id tags]} transform
        unsaved-transform @(re-posh/subscribe [:unsaved-transform/pull [:unsaved-transform/id id]])
        {:unsaved-transform/keys [input-tables output-tables]} unsaved-transform]
    {:http-xhrio
     {:id     :update-transform-request
      :url    (str "/api/transformations/environments/" environment-id "/" group-id "/transforms/" id)
      :method :put
      :params (-> transform
                  (dissoc :id)
                  utils/clean-up-query-recursive
                  (update :tags select-tables tags)
                  (update :input-tables select-tables input-tables)
                  (update :output-tables select-tables output-tables))
      :cb-params (assoc params :environment-id environment-id)}}))

(defn update-transform-callback [_ [error resp {:keys [transform group-id environment-id on-success]}]]
  (cond
    (= 409 (:status error))
    {:dispatch-n [[:app.events/add-snack
                   :warning
                   "Someone has already updated this transform while you were editing it.\nPlease save your changes, and reload the page to get the latest version."]
                  [:transform/update [:transform/id (:id transform)] {:transform/stale true}]]}

    (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to update the transform"]
                  [:app.api/log :error "Failed to update the transform" error]]}

    :otherwise
    (let [old-transform-id    (:id transform)
          schedule-capture    (schedule-change-capture "transform"
                                                       @(re-posh/subscribe [:transform/prop [:transform/id old-transform-id] :transform/schedule-spec])
                                                       (:schedule-spec transform))
          transform           (rename-keys (:result resp) {:transform-id :id})
          transform-id        (:id transform)
          modification-states @(re-posh/subscribe [:environments-panel/prop :environments-panel/modification-states])

          {:transforms-panel/keys [transforms]}
          @(re-posh/subscribe [:transforms-panel/pull '[{:transforms-panel/transforms [*]}]])
          updated-transforms (mapv (fn [{:transform/keys [id] :as t}]
                                (if (= id old-transform-id)
                                  (->transform transform) t))
                              transforms)]

      (when on-success (on-success))

      {:fx [[:posthog/capture {:event (:transform/updated posthog-events/events)
                               :props {:environment-id environment-id}}]
            (when schedule-capture
              [:posthog/capture (assoc-in schedule-capture [:props :environment-id] environment-id)])]
       :dispatch-n [[:unsaved-transform/delete [:unsaved-transform/id transform-id]]
                    [:transform/add (->transform transform)]
                    [:environments-panel/update (->environments-panel {:modification-states
                                                                       (update modification-states
                                                                               (keyword group-id) #(or (some-> % (conj "deleted")) ["directly-modified"]))})]
                    [:transforms-panel/update {:transforms-panel/transforms updated-transforms} {:replace true}]
                    [:sql-console-modal.events/update-transform-ids old-transform-id transform]
                    [:app.events/add-snack :success "Transform updated"]
                    [:transformations.api/cyclic-deps-check]]})))

(reg-event-http
 :environments.api/update-environment-transform
 update-transform-request
 update-transform-callback)

(reg-event-fx
 :environments.api/update-transform
 (fn [_ [_ params]]
   (let [prod-env? (or (and (contains? params :environment-id) (not (:environment-id params)))
                       @(re-posh/subscribe [:environments.subs/production-active? (:environment-id params)]))]
     (if prod-env?
       {:dispatch [:transform.api/update-transform (assoc params :redirect? false)]}
       {:dispatch [:environments.api/update-environment-transform params]}))))



;; =============================================================================
;; Unschedule Transform
;; =============================================================================

(defn unschedule-transform-request [_ [{:keys [group-id transform on-success]}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    {:http-xhrio
     {:url       (str "/api/transformations/environments/" environment-id "/" group-id "/transforms/" (:id transform))
      :method    :put
      :params    (-> transform
                     (dissoc :id :schedule-spec)
                     utils/clean-up-query-recursive
                     (update :tags select-tables nil)
                     (update :input-tables select-tables nil)
                     (update :output-tables select-tables nil))
      :cb-params {:group-id       group-id
                  :transform-id   (:id transform)
                  :environment-id environment-id
                  :on-success     on-success}}}))

(defn unschedule-transform-callback [_ [error result {:keys [group-id transform-id environment-id on-success]}]]
  (cond
    (= 409 (:status error))
    {:dispatch-n [[:app.events/add-snack
                   :warning
                   "Someone has already updated this transform while you were editing it.
                    Please save your changes, and reload the page to get the latest version."]
                  [:transform/update [:transform/id transform-id] {:transform/stale true}]]}

    (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to unschedule transform"]
                  [:app.api/log :error "Failed to unschedule transform" error]]}

    :otherwise
    (let [{:keys [id] :as transform} (:data result)]
      (when on-success
        (on-success {:transform transform}))
      {:dispatch-n [[:transform/update [:transform/id transform-id] (->transform transform) {:replace true}]
                    [:app.events/add-snack :success "Transform unscheduled"]]
       :posthog/capture {:event (:transform/schedule-updated posthog-events/events)
                         :props {:scope          "transform"
                                 :action         "unscheduled"
                                 :environment-id environment-id}}})))

(reg-event-http
 :environments.api/unschedule-environment-transform
 unschedule-transform-request
 unschedule-transform-callback)

(reg-event-fx
 :environments.api/unschedule-transform
 (fn [_ [_ params]]
   (let [prod-env? @(re-posh/subscribe [:environments.subs/production-active?])]
     (if prod-env?
       {:dispatch [:transform.api/unschedule-transform (assoc params :redirect? false)]}
       {:dispatch [:environments.api/unschedule-environment-transform params]}))))

;; =============================================================================
;; Fetch transform diff
;; =============================================================================

(defn fetch-transform-diff-request [_ [{:keys [group-id transform-id] :as params}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    {:http-xhrio
     {:id     :fetch-transform-diff-request
      :url    (str "/api/transformations/environments/" environment-id "/" group-id "/transforms/" transform-id "/diff")
      :method :get
      :cb-params params}}))

(defn fetch-transform-diff-callback [_ [error resp {:keys [transform-id group-id on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch transforms"]]}

    (let [diff (->transform-diff (-> (:result resp)
                                     (assoc
                                      :transform-id transform-id
                                      :group-id     group-id)
                                     (update :transform-id-in-master #(or % ""))
                                     (update :master-transform-sql #(or % ""))))]
      (when on-success (on-success))
      {:dispatch-n [[:transform-diff/add diff]]})))

(reg-event-http
 :environments.api/fetch-transform-diff
 fetch-transform-diff-request
 fetch-transform-diff-callback)


;; =============================================================================
;; Merge environment
;; =============================================================================

(defn merge-environment-request [_ [{:keys [transform-ids] :as params}]]
  (let [environment-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    {:http-xhrio
     {:id     :merge-environments-request
      :url    (str "/api/transformations/environments/" environment-id "/merge")
      :method :post
      :params {:transform-ids transform-ids}
      :cb-params (assoc params :environment-id environment-id)}}))

(defn merge-environment-callback [_ [error _ {:keys [transform-ids environment-id on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to merge environment"]]}

    (do
      (when on-success (on-success))
      {:dispatch [:app.events/add-snack :success "Environment merged successfully"]
       :posthog/capture {:event (:transform-environment/merged posthog-events/events)
                         :props {:environment-id  environment-id
                                 :transform-count (count transform-ids)}}})))

(reg-event-http
 :environments.api/merge-environment
 merge-environment-request
 merge-environment-callback)



;; =============================================================================
;; Restore environment
;; =============================================================================

(defn restore-environment-request [_ [{:keys [environment-id] :as params}]]
  {:http-xhrio
   {:id     :restore-environments-request
    :url    (str "/api/transformations/environments/" environment-id "/rollback")
    :method :post
    :cb-params params}})

(defn restore-environment-callback [_ [error _ {:keys [on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to restore transform"]]}

    (do
      (when on-success (on-success))
      {:dispatch [:app.events/add-snack :success "Production restored successfully"]})))

(reg-event-http
 :environments.api/restore-environment
 restore-environment-request
 restore-environment-callback)

(defn fetch-params [node]
  (cond
    (:column-name node) nil
    (:table-name node)  {:object "columns"
                         :catalog (or (:table-catalog node) (:table-cat node))
                         :schema  (:table-schem node)
                         :table   (:table-name node)}
    (:table-schem node) {:object "tables"
                         :catalog (or (:table-catalog node) (:table-cat node))
                         :schema (:table-schem node)}
    (:table-cat node)   {:object "schemas"
                         :catalog (:table-cat node)}))


(defn split-table-name
  "Converts table into a request params for fetching parent node metadata. Provides default values
  for database name and schema, when necessary.

  Expects a fully qualified table name: `DB.SCHEMA.TABLE` or `SCHEMA.TABLE`.

  Returns `nil` when `table` params does look like a fully qualified table name."
  [table]
  (let [name-parts (clojure.string/split table #"\.")
        number-of-parts (count name-parts)]
    (when (contains? #{2 3} number-of-parts)
      (let [parts (take 3 (concat (reverse name-parts) (repeat nil)))
            [_table-name schema catalog] parts

            warehouse-type  @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])
            snowflake? (= warehouse-type "snowflake")

            default-db @(re-posh/subscribe [:connections.subs/warehouse-db-name])

            default-schema "default"

            node {:table-schem (or schema default-schema)
                  :table-cat   (or catalog default-db)}]
        (fetch-params node)))))


(defn generate-table-name [node]
  (str (:table-cat node) "." (:table-schem node) "." (:table-name node)))

;; =============================================================================
;; Get all input tables
;; =============================================================================

(defn fetch-all-input-tables-request [_ []]
  {:http-xhrio
   {:id     :all-input-tables-request
    :url    "/api/transformations/input-tables/all"
    :method :get}})

(defn fetch-all-input-tables-callback [_ [error response {:keys [on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch the list of input tables"]]}

    (let [tables (:result response)
          metadata-params (distinct (filter some? (map split-table-name tables)))]
      {:dispatch-n (map (fn [params]
                          [:sql-console.api/fetch-metadata
                           params
                           {:on-success (fn [resp]
                                          (let [available-tables (set (map generate-table-name resp))
                                                tables (keep (fn [table] (when (contains? available-tables table) table)) tables)]
                                            (dispatch [:environments.events/add-all-input-tables tables])))}])
                        metadata-params)})))

(reg-event-http
 :environments.api/fetch-all-input-tables
 fetch-all-input-tables-request
 fetch-all-input-tables-callback)


;; =============================================================================
;; Get ingest input tables
;; =============================================================================

(defn fetch-initial-input-tables-request [_ []]
  {:http-xhrio
   {:id     :all-input-tables-request
    :url    "/api/transformations/input-tables/initial"
    :method :get}})

(defn fetch-initial-input-tables-callback [_ [error response {:keys [on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch the list of input tables"]]}

    (let [tables (:result response)
          metadata-params (distinct (filter some? (map split-table-name tables)))]
      {:dispatch-n (map (fn [params]
                          [:sql-console.api/fetch-metadata
                           params
                           {:on-success (fn [resp]
                                          (let [available-tables (set (map generate-table-name resp))]
                                            (doall
                                             (for [table tables]
                                               (when (contains? available-tables table)
                                                 (dispatch [:environments.events/add-initial-input-table table]))))))}])
                        metadata-params)})))

(reg-event-http
 :environments.api/fetch-initial-input-tables
 fetch-initial-input-tables-request
 fetch-initial-input-tables-callback)


;; =============================================================================
;; Copy selected tables
;; =============================================================================

(defn copy-tables-request [_ [{:keys [tables environment-id] :as params}]]
  {:http-xhrio
   {:id     :copy-tables-request
    :params {:tables tables}
    :url    (str "/api/transformations/environments/" environment-id "/copy-tables")
    :method :post
    :cb-params {:environment-id environment-id}}})

(defn copy-tables-callback [_ [error response {:keys [environment-id on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to copy tables"]]}

    (let [table-count (count (:result response))]
      {:dispatch-n [[:app.events/add-snack :success (str "Finished copying " table-count (if (= 1 table-count) " table" " tables") " to the new environment")]]
       :posthog/capture {:event (:transform-environment/data-copied posthog-events/events)
                         :props {:environment-id environment-id
                                 :table-count    table-count}}})))

(reg-event-http
 :environments.api/copy-tables
 copy-tables-request
 copy-tables-callback)


;; =============================================================================
;; Get Latest Transform Version
;; =============================================================================

(defn get-latest-version-request [_ [{:keys [master-id old-transform-id group-id environment-id]}]]
  (let [environment-id (or environment-id
                           @(re-posh/subscribe [:environments.subs/active-environment-id]))
        master-id (or master-id
                      @(re-posh/subscribe [:transform/prop [:transform/id old-transform-id] :transform/master-id]))]
    {:http-xhrio
     {:url       (str "/api/transformations/environments/" environment-id "/latest-transform-version")
      :method    :get
      :params    {:master-id master-id}}}))

(defn get-latest-version-callback [_ [error resp]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch transform"]
                  [:app.api/log :error "Failed to fetch transform" error]]}

    (let [transform (:result resp)
          transform (assoc transform :id (:transform-id transform))]
      {:dispatch-n [[:transform/update [:transform/id (:id transform)] (->transform transform) {:replace true}]]})))

(reg-event-http
 :environments.api/get-environment-transform-latest-version-by-master-id
 get-latest-version-request
 get-latest-version-callback)

(reg-event-fx
 :environments.api/get-latest-version-by-master-id
 (fn [_ [_ params]]
   (let [prod-env? (or (and (contains? params :environment-id) (not (:environment-id params)))
                       @(re-posh/subscribe [:environments.subs/production-active? (:environment-id params)]))]
     (if prod-env?
       {:dispatch [:transform.api/get-latest-version-by-master-id params]}
       {:dispatch [:environments.api/get-environment-transform-latest-version-by-master-id params]}))))




(defn get-latest-version-callback-preview [_ [error resp {:keys [transform-id] :as cb}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch transform"]
                  [:app.api/log :error "Failed to fetch transform" error]]}

    (let [transform (:result resp)
          transform (assoc transform :id (:transform-id transform))]
      {:dispatch-n [[:transform.events/save-transform-production-preview (:master-id transform) transform]

                    (when transform-id
                      [:sql-console-modal.events/update-transform-ids transform-id (assoc transform
                                                                                          :transform-id (:id transform))])]})))

(reg-event-http
 :environments.api/get-environment-transform-latest-version-preview
 get-latest-version-request
 get-latest-version-callback-preview)

(reg-event-fx
 :environments.api/get-latest-version-by-master-id-preview
 (fn [_ [_ params]]
   (let [prod-env? (or (and (contains? params :environment-id) (not (:environment-id params)))
                       @(re-posh/subscribe [:environments.subs/production-active? (:environment-id params)]))]
     (if prod-env?
       {:dispatch [:transform.api/get-latest-version-by-master-id-preview params]}
       {:dispatch [:environments.api/get-environment-transform-latest-version-preview params]}))))
